import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Input,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
	Avatar,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiUserDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetCustomerList, requestDeleteCustomer } from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

function CustomersListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [customers, setCustomers] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
		role: searchParams.get("role") || "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Users";
		fetchCustomers();
	}, []);

	useEffect(() => {
		fetchCustomers();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);
		if (filters.role) params.set("role", filters.role);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchCustomers = () => {
		setLoading(true);
		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			order: "name:asc",
		};

		// Build filter string
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`deleted:${filters.status === "DELETED" ? "true" : "false"}`);
		}
		if (filters.role) {
			filterParts.push(`role:${filters.role}`);
		}
		if (filterParts.length > 0) {
			params.filter = filterParts.join(",");
		}

		requestGetCustomerList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const customersData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setCustomers(customersData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching users:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch users",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDeleteCustomer = (id) => {
		requestDeleteCustomer(id)
			.then(() => {
				message.success("User deleted successfully");
				fetchCustomers();
			})
			.catch((error) => {
				console.error("Error deleting user:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to delete user. Please try again.",
					});
				}
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusFilter = (status) => {
		setFilters((prev) => ({ ...prev, status }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleRoleFilter = (role) => {
		setFilters((prev) => ({ ...prev, role }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const columns = [
		{
			title: "Customer",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<Avatar
						size={40}
						icon={<PiUserDuotone />}
						src={record.avatar}
						style={{ marginRight: 12 }}
					/>
					<div>
						<Space size={0}>
							<div style={{ fontWeight: 500 }}>{text}</div>
							{record.role === "ADMIN" && (
								<Tag color="red" style={{ marginLeft: 8 }}>
									Admin
								</Tag>
							)}
							{record.role === "USER" && (
								<Tag color="blue" style={{ marginLeft: 8 }}>
									User
								</Tag>
							)}
						</Space>
						<div style={{ fontSize: "12px", color: "#666" }}>{record.email}</div>
					</div>
				</div>
			),
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			render: (text) => text || "-",
		},
		{
			title: "Teams",
			dataIndex: "teamIds",
			key: "teamIds",
			render: (teamIds, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>
						{teamIds && teamIds.length > 0 ? `${teamIds.length} Team(s)` : "No Teams"}
					</div>
					{record.defaultTeamId && (
						// <div style={{ fontSize: "12px", color: "#666" }}>
						// 	Default: {record.defaultTeamId.substring(0, 8)}...
						// </div>
						<Button
							type="link"
							size="small"
							onClick={() => navigate(`/teams/${record.defaultTeamId}`)}
							style={{ padding: 0, fontSize: "12px" }}>
							Default: {record.defaultTeamId.substring(0, 8)}...
						</Button>
					)}
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "deleted",
			key: "status",
			render: (deleted) => (
				<Tag color={deleted ? "red" : "green"}>{deleted ? "Deleted" : "Active"}</Tag>
			),
		},
		{
			title: "Date joined",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => dayjs(date).format("DD MMM YYYY"),
		},
		{
			title: "Action",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Button
						type="text"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/customers/${record._id}`)}
						title="View"
					/>
					<Button
						type="text"
						size="small"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/customers/${record._id}/edit`)}
						title="Edit"
					/>
					<Popconfirm
						title="Delete User"
						description="Are you sure you want to delete this user?"
						onConfirm={() => handleDeleteCustomer(record._id)}
						okText="Yes"
						cancelText="No"
						okType="danger">
						<Button type="text" size="small" danger icon={<PiTrashDuotone />} title="Delete" />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						User Management
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="All Users"
					extra={
						<Button
							type="primary"
							icon={<PiPlusDuotone />}
							onClick={() => navigate("/customers/add")}>
							Add User
						</Button>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={8}>
							<SearchInput
								placeholder="Search customers..."
								value={filters.keyword}
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Filter by status"
								value={filters.status || undefined}
								onChange={handleStatusFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="ACTIVE">Active</Option>
								<Option value="DELETED">Deleted</Option>
							</Select>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Filter by role"
								value={filters.role || undefined}
								onChange={handleRoleFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="USER">User</Option>
								<Option value="ADMIN">Admin</Option>
							</Select>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={customers}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
						}}
						onChange={handleTableChange}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default CustomersListPage;
